"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { type ExamType, EXAM_CONFIGS } from "@/lib/exam/config";
import ExamHubSidebar from "./exam-hub-sidebar";
import PracticePage from "./practice-page";

interface ExamHubLayoutProps {
  examType: ExamType;
}

/**
 * Layout for the per-exam hub: sidebar on the left, PracticePage on the right.
 *
 * On desktop (`lg+`), the sidebar is permanently visible (its own `hidden
 * lg:block` toggle).
 *
 * On mobile (`<lg`), the sidebar is replaced by a "Menu" button that opens
 * a slide-in drawer containing the same content. The drawer auto-closes on
 * route change so navigating from Study Guide → FAQ doesn't leave it open,
 * and Escape closes it for keyboard users.
 */
export default function ExamHubLayout({ examType }: ExamHubLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = usePathname();
  const config = EXAM_CONFIGS[examType];

  // Auto-close drawer when route changes (link inside drawer was clicked)
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // ESC closes the drawer
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setDrawerOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <div className="min-h-screen bg-cream dark:bg-gray-950">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Mobile menu trigger */}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="lg:hidden flex items-center gap-2 mb-5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          aria-label={`Open ${config.name} menu`}
          aria-expanded={drawerOpen}
        >
          <Menu size={16} />
          <config.icon size={16} strokeWidth={1.75} aria-hidden="true" />
          <span>{config.name} menu</span>
        </button>

        <div className="flex gap-8">
          <ExamHubSidebar examType={examType} />
          <main className="flex-1 min-w-0">
            <PracticePage examType={examType} embedded />
          </main>
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer panel */}
          <div className="relative ml-0 mr-auto w-72 max-w-[85vw] h-full bg-white dark:bg-gray-900 shadow-xl overflow-y-auto motion-safe:animate-drawer-slide-in">
            <div className="flex items-center justify-between px-4 pt-4 pb-2 sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">Menu</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            {/* Reuse the same sidebar component — its hidden lg:block class
                only affects the outer aside; force it back to block + full
                width when rendered inside this drawer wrapper. */}
            <div className="px-3 py-4 [&>aside]:!block [&>aside]:!w-full">
              <ExamHubSidebar examType={examType} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
